import APIService from "./APIService";

/**
 * API service for image analysis operations
 */
class ImageAnalysisAPI {
  /**
   * Search image analyses with filters
   * @param {Object} criteria - Search criteria
   * @returns {Promise<Array>} Array of image analyses
   */
  async searchAnalyses(criteria = {}) {
    try {
      const response = await APIService.post(
        "/api/v1/scam/analysis/image/search",
        criteria,
      );
      return response;
    } catch (error) {
      console.error("Error searching analyses:", error);
      throw error;
    }
  }

  /**
   * Get single image analysis by upload ID
   * @param {string} uploadId - Upload ID
   * @returns {Promise<Object>} Image analysis details
   */
  async getAnalysis(uploadId) {
    try {
      const response = await APIService.get(
        `/api/v1/scam/analysis/image?uploadId=${encodeURIComponent(uploadId)}`,
      );
      return response;
    } catch (error) {
      console.error("Error getting analysis:", error);
      throw error;
    }
  }

  /**
   * Check if analysis exists
   * @param {string} uploadId - Upload ID
   * @returns {Promise<boolean>} True if exists
   */
  async analysisExists(uploadId) {
    try {
      const response = await APIService.get(
        `/api/v1/scam/analysis/image/exists?uploadId=${encodeURIComponent(
          uploadId,
        )}`,
      );
      return response.exists;
    } catch (error) {
      console.error("Error checking analysis existence:", error);
      return false;
    }
  }

  /**
   * Delete image analysis
   * @param {string} uploadId - Upload ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteAnalysis(uploadId) {
    try {
      const response = await APIService.delete(
        `/api/v1/scam/analysis/image?uploadId=${encodeURIComponent(uploadId)}`,
      );
      return response;
    } catch (error) {
      console.error("Error deleting analysis:", error);
      throw error;
    }
  }

  /**
   * Get analyses by date range
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<Array>} Array of analyses
   */
  async getAnalysesByDateRange(startDate, endDate) {
    return this.searchAnalyses({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
  }

  /**
   * Get scam analyses only
   * @returns {Promise<Array>} Array of scam analyses
   */
  async getScamAnalyses() {
    return this.searchAnalyses({
      isScam: true,
    });
  }

  /**
   * Get safe analyses only
   * @returns {Promise<Array>} Array of safe analyses
   */
  async getSafeAnalyses() {
    return this.searchAnalyses({
      isScam: false,
    });
  }

  /**
   * Get high confidence scams
   * @param {number} minConfidence - Minimum confidence (0-1)
   * @returns {Promise<Array>} Array of high confidence scam analyses
   */
  async getHighConfidenceScams(minConfidence = 0.8) {
    return this.searchAnalyses({
      isScam: true,
      minConfidence,
    });
  }

  /**
   * Search by filename
   * @param {string} filename - Filename to search
   * @returns {Promise<Array>} Array of matching analyses
   */
  async searchByFilename(filename) {
    return this.searchAnalyses({
      filename,
    });
  }

  /**
   * Get recent analyses
   * @param {number} days - Number of days to look back
   * @param {number} limit - Maximum results
   * @returns {Promise<Array>} Array of recent analyses
   */
  async getRecentAnalyses(days = 7, limit = 50) {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    return this.searchAnalyses({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      limit,
    });
  }

  /**
   * Get analyses by scam type
   * @param {string} scamType - Scam type
   * @returns {Promise<Array>} Array of analyses
   */
  async getAnalysesByScamType(scamType) {
    return this.searchAnalyses({
      scamType,
    });
  }

  /**
   * Get statistics
   * @returns {Promise<Object>} Statistics object
   */
  async getStatistics() {
    try {
      const allAnalyses = await this.searchAnalyses({});

      const stats = {
        total: allAnalyses.length,
        scams: allAnalyses.filter((a) => a.scam === true || a.isScam === true)
          .length,
        safe: allAnalyses.filter((a) => a.scam === false || a.isScam === false)
          .length,
        avgConfidence: this.calculateAvgConfidence(allAnalyses),
        scamTypes: this.groupByScamType(allAnalyses),
        recentScams: allAnalyses.filter(
          (a) =>
            (a.scam === true || a.isScam === true) &&
            this.isRecent(a.analyzedAt, 7),
        ).length,
      };

      return stats;
    } catch (error) {
      console.error("Error getting statistics:", error);
      throw error;
    }
  }

  /**
   * Calculate average confidence
   */
  calculateAvgConfidence(analyses) {
    if (analyses.length === 0) return 0;
    const sum = analyses.reduce((acc, a) => acc + (a.confidence || 0), 0);
    return sum / analyses.length;
  }

  /**
   * Group analyses by scam type
   */
  groupByScamType(analyses) {
    const groups = {};
    analyses
      .filter((a) => a.scam === true || a.isScam === true)
      .forEach((a) => {
        const type = a.scamType || "UNKNOWN";
        groups[type] = (groups[type] || 0) + 1;
      });
    return groups;
  }

  /**
   * Check if date is within days from now
   */
  isRecent(dateString, days) {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= days;
  }
}

export default new ImageAnalysisAPI();
